import {App} from 'vue';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginVue from '@bugsnag/plugin-vue';
import BugsnagPerformance from '@bugsnag/browser-performance';
import ServerConfig from '../config/server';
import {drainEarlyErrors} from '../helpers/earlyErrors';

let bugsnagInitialised = false;

const ERROR_RELEASE_STAGES = ['production', 'staging', 'testing', 'local'];

// No local: dev-server timings would only skew the dashboards.
const PERFORMANCE_RELEASE_STAGES = ['production', 'staging', 'testing'];

// The ad-block probe requests a URL blockers reject; without this it lands
// inside the settle window and pads every page-load duration.
const SETTLE_IGNORE_URLS = [/pagead2\.googlesyndication\.com/];

export const initialiseBugsnag = () => {
    if (bugsnagInitialised) {
        return;
    }

    // The bundle bakes env=production into every image, so the stage comes
    // from the server. appVersion stays baked: it must match the sourcemaps.
    const releaseStage =
        document.querySelector('meta[name="app-env"]')?.getAttribute('content') || ServerConfig.env;

    // Eager: connectVue needs the Vue plugin synchronously.
    Bugsnag.start({
        apiKey: ServerConfig.bugsnagApi,
        plugins: [new BugsnagPluginVue()],
        releaseStage,
        appVersion: ServerConfig.buildNumber,
        enabledReleaseStages: ERROR_RELEASE_STAGES,
    });

    // Must follow start() immediately, or anything landing between the two
    // handlers is reported twice. notify() defaults to a handled warning, so
    // these need severity set to match the notifier's own unhandled errors.
    drainEarlyErrors().forEach((error) => Bugsnag.notify(error, (event) => {
        event.unhandled = true;
        event.severity = 'error';

        if (!(error instanceof Error)) {
            event.errors[0].stacktrace = [];
        }
    }));

    // start() POSTs for the sampling probability even when every span is
    // dropped, so skip it entirely on a disabled stage. Eager: the fetch and
    // XHR trackers only feed spans from start() on, so deferring it loses an
    // [HTTP] span for every request the page makes before that point.
    if (PERFORMANCE_RELEASE_STAGES.includes(releaseStage)) {
        BugsnagPerformance.start({
            apiKey: ServerConfig.bugsnagApi,
            releaseStage,
            appVersion: ServerConfig.buildNumber,
            enabledReleaseStages: PERFORMANCE_RELEASE_STAGES,
            settleIgnoreUrls: SETTLE_IGNORE_URLS,
        });
    }

    bugsnagInitialised = true;
};

export const connectVue = (app: App) => {
    initialiseBugsnag();

    const plugin = Bugsnag.getPlugin('vue');

    if (plugin) {
        app.use(plugin);
    }
};
